<!--
  User Mentions List Component

  Fetches and displays users for @ mentions with @everyone support.
  Handles both channel members (private) and group members (public).
-->

<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, watch } from 'vue'

  import CPAvatar from '@packages/ui/components/CPAvatar.vue'

  import CPSkeleton from '@packages/ui/components/CPSkeleton.vue'

  import CPText from '@packages/ui/components/CPText.vue'

  import { useI18n } from 'vue-i18n'

  import { Users01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { useCommunityUser } from '@communities/community-user-layer/composables/useCommunityUser'

  import { useGroups } from '@communities/groups-layer/composables/useGroups'

  import { GroupMemberRole } from '@communities/groups-layer/types/types'

  import { useChannelMembers } from '@communities/channels-layer/composables/useChannelMembers'

  import { useMembers } from '@communities/members-layer/composables/useMembers'

  import { useKeyboardNavigation } from '../composables/useKeyboardNavigation'
  import type { GroupMember, UserMention } from './types'

  const props = defineProps({
    query: {
      type: String,
      default: '',
    },
    command: {
      type: Function,
      required: true,
    },
    // Optional channel context for private channels
    channelId: {
      type: String,
      default: undefined,
    },
    isPrivateChannel: {
      type: Boolean,
      default: false,
    },
  })

  // Composables
  const { t } = useI18n()

  const groups = useGroups()

  const currentGroupId = computed(() => groups.store.currentGroup?._id || '')

  const currentLocationId = computed(() => groups.store.currentGroup?.locationId || '')

  // Initialize members composable
  const members = useMembers(currentGroupId, currentLocationId)

  const channelMembers = useChannelMembers(
    currentGroupId,
    computed(() => props.channelId || '')
  )

  // Get current user's role in the group
  const communityUser = useCommunityUser()

  const userGroupDetails = communityUser.fetchCurrentUserDetailsForGroup(currentGroupId, currentLocationId)

  // Check if user can mention everyone (only admins and owners)
  const canMentionEveryone = computed(() => {
    const userRole = userGroupDetails.data.value?.role
    return userRole === GroupMemberRole.ADMIN || userRole === GroupMemberRole.OWNER
  })

  // Debounced search query
  const debouncedQuery = ref('')

  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  // Reactive params for fetching
  const publicMemberParams = computed(() => ({
    search: debouncedQuery.value || undefined,
  }))

  const privateMemberParams = computed(() => ({
    limit: 10,
    skip: 0,
    search: debouncedQuery.value || undefined,
  }))

  // Create queries immediately (not wrapped in computed)
  // This ensures they fetch on mount like channels/events do
  const privateChannelQuery = channelMembers.getSuggestions(privateMemberParams)

  const publicChannelQuery = members.fetchGroupMembersInfiniteQuery(publicMemberParams)

  const isPrivateContext = computed(() => props.isPrivateChannel && !!props.channelId)

  // Loading state
  const isLoading = computed(() => {
    return isPrivateContext.value ? privateChannelQuery.isLoading.value : publicChannelQuery.isLoading.value
  })

  // Transform API response to UserMention format
  const users = computed((): UserMention[] => {
    const rawData = isPrivateContext.value ? privateChannelQuery.allData.value : publicChannelQuery.allData.value

    if (!rawData) {
      return []
    }

    // Handle different response formats
    let members: GroupMember[] = []

    if (Array.isArray(rawData)) {
      members = rawData as GroupMember[]
    } else if (typeof rawData === 'object' && 'data' in rawData) {
      // API might wrap in { data: [...] }
      members = (rawData as { data: GroupMember[] }).data || []
    }

    const seenContactIds = new Set<string>()
    return members.flatMap(member => {
      if (seenContactIds.has(member.contactId)) {
        return []
      }
      seenContactIds.add(member.contactId)
      return [
        {
          id: member.contactId,
          contactId: member.contactId,
          label: member.fullName || member.name || '',
          fullName: member.fullName || member.name || '',
          slug: member.slug,
          avatar: member.avatar,
          role: member.role,
          type: 'user' as const,
        },
      ]
    })
  })

  // Add @everyone if applicable
  const allSuggestions = computed((): UserMention[] => {
    const result: UserMention[] = []

    // Add @everyone first if user has permission and it matches query
    if (canMentionEveryone.value) {
      const everyoneLabel = t('editor.mentions.everyone')
      if (everyoneLabel.toLowerCase().includes(props.query.trim().toLowerCase())) {
        result.push({
          label: everyoneLabel,
          type: 'broadcast',
        })
      }
    }

    // Add users
    result.push(...users.value)

    return result
  })

  // No matches once loading has settled — the popup should disappear entirely
  // rather than show an empty state (see PostEditor "@rahul test" no-match case).
  const hasNoResults = computed(() => {
    return !isLoading.value && allSuggestions.value.length === 0
  })

  // Handle selection
  const handleSelect = (user: UserMention) => {
    if (user.type === 'broadcast') {
      props.command({
        label: user.label,
        type: 'broadcast',
      })
    } else {
      props.command({
        id: user.contactId,
        label: user.fullName || user.label,
        slug: user.slug,
        type: 'user',
      })
    }
  }

  // Keyboard navigation using shared composable
  const { selectedIndex, handleKeyDown } = useKeyboardNavigation(allSuggestions, handleSelect)

  // Auto-scroll selected item into view
  const userListRef = ref<HTMLElement>()
  const loadMoreSentinelRef = ref<HTMLElement>()
  let loadMoreObserver: IntersectionObserver | null = null

  const hasNextPage = computed(() => !isPrivateContext.value && publicChannelQuery.hasNextPage.value)

  const observeLoadMoreSentinel = (sentinel?: HTMLElement) => {
    loadMoreObserver?.disconnect()
    loadMoreObserver = null

    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      return
    }

    loadMoreObserver = new IntersectionObserver(entries => {
      if (
        entries.some(entry => entry.isIntersecting) &&
        hasNextPage.value &&
        !publicChannelQuery.isFetchingNextPage.value
      ) {
        void publicChannelQuery.fetchNextPage()
      }
    })
    loadMoreObserver.observe(sentinel)
  }

  // Cleanup
  onBeforeUnmount(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
    loadMoreObserver?.disconnect()
  })

  // Watch for query changes with debounce (300ms)
  watch(
    () => props.query,
    newQuery => {
      // Clear existing timer
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }

      // Update immediately if query is empty (for initial load)
      if (!newQuery) {
        debouncedQuery.value = ''
      } else {
        // Debounce for search queries
        debounceTimer = setTimeout(() => {
          debouncedQuery.value = newQuery.trim()
        }, 300)
      }
    },
    { immediate: true }
  )

  watch(loadMoreSentinelRef, observeLoadMoreSentinel, { flush: 'post' })

  watch(selectedIndex, () => {
    if (userListRef.value) {
      const selectedElement = userListRef.value.querySelector('.user-item.selected')
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' })
      }
    }
  })

  // Expose keyboard handler + selected row for TipTap suggestion Enter (same pattern as HashMentionsList)
  defineExpose({
    handleKeyDown,
    getSelectedItem: () => allSuggestions.value[selectedIndex.value] ?? null,
  })
</script>

<template>
  <div class="suggestions-container" :class="{ 'is-hidden': hasNoResults }">
    <div class="mentions-list">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="space-y-2 p-3">
          <div v-for="i in 3" :key="i" class="flex items-center gap-3 px-2">
            <CPSkeleton class="w-7 h-7 rounded-full" />
            <CPSkeleton class="h-4 w-32" />
          </div>
        </div>
      </div>

      <!-- User List -->
      <div v-else ref="userListRef" class="user-list">
        <div
          v-for="(user, index) in allSuggestions"
          :id="user.contactId ? `user-mention-option-${user.contactId}` : 'user-mention-option-broadcast'"
          :key="user.id || user.label"
          :class="['user-item', { selected: index === selectedIndex }]"
          @mousedown.prevent
          @click="handleSelect(user)"
        >
          <!-- @everyone -->
          <template v-if="user.type === 'broadcast'">
            <div class="flex items-center gap-3 px-4 py-2">
              <div class="w-8 h-8 bg-hover rounded-full flex items-center justify-center">
                <Users01Icon class="w-5 h-5 text-600" />
              </div>
              <div class="flex-1 min-w-0">
                <CPText variant="lg" weight="semibold" class="text-700">{{ user.label }}</CPText>
                <CPText variant="sm" weight="medium" class="text-600">
                  {{ t('editor.mentions.notifyAllMembers') }}
                </CPText>
              </div>
            </div>
          </template>

          <!-- Regular User -->
          <template v-else>
            <div class="flex items-center gap-3 px-4 py-2">
              <CPAvatar :src="user.avatar" :name="user.fullName || user.label" size="sm" />
              <div class="flex-1 min-w-0">
                <CPText variant="lg" weight="semibold" class="text-700 truncate">
                  {{ user.fullName || user.label }}
                </CPText>
              </div>
            </div>
          </template>
        </div>
        <div
          v-if="hasNextPage"
          ref="loadMoreSentinelRef"
          data-testid="mention-load-more-sentinel"
          aria-hidden="true"
          class="h-px"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
  .suggestions-container {
    /* Ensure dropdown appears above modal */
    z-index: 10000 !important;
    position: relative !important;

    /* Proper dropdown styling */
    background: var(--cp-brand-background);
    border: 1px solid var(--cp-border-600);
    border-radius: 8px;
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
    width: 20rem;
  }

  /* No matches — collapse instead of showing an empty box. Kept mounted (not v-if)
     so the ResizeObserver in userMentions.ts keeps a stable Element to observe. */
  .suggestions-container.is-hidden {
    display: none;
  }

  .mentions-list {
    background: var(--cp-brand-background);
    border-radius: 8px;
    overflow: hidden;
    max-height: 20rem;
    overflow-y: auto;
  }

  .user-item {
    cursor: pointer;
    transition: background-color 0.15s ease;
  }

  .user-item:hover,
  .user-item.selected {
    background-color: var(--cp-font-100);
    border: none;
  }

  .user-item:active {
    background-color: var(--cp-brand-surface);
  }

  /* Loading state */
  .loading-state {
    background: var(--cp-brand-background);
  }

  /* Scrollbar styling */
  .mentions-list::-webkit-scrollbar {
    width: 4px;
  }

  .mentions-list::-webkit-scrollbar-track {
    background: var(--cp-brand-surface);
  }

  .mentions-list::-webkit-scrollbar-thumb {
    background: var(--cp-border-600);
    border-radius: 2px;
  }

  .mentions-list::-webkit-scrollbar-thumb:hover {
    background: var(--cp-font-400);
  }
</style>
